// src/components/auth/AssetAccessPrompt.jsx
// -----------------------------------------------------------------------------
// Modal access states for Asset Library / Asset Studio entry points.
//
// This component is intentionally presentation-only:
// - It does not fetch assets.
// - It does not initialize auth.
// - It does not navigate unless the caller provides an action.
// - It does not own the outer MUI Dialog shell.
//
// AssetLibrary owns the Dialog and decides which state to render.
// This component renders DialogTitle/DialogContent/DialogActions so the modal
// keeps correct MUI hierarchy, accessibility labels, and action placement.
// -----------------------------------------------------------------------------

import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

const PROMPT_COPY = {
  standaloneLogin: {
    icon: LoginIcon,
    title: 'Sign in to access Asset Studio',
    body:
      'Log in to your organization account to choose an asset from your ' +
      'library and attach it to this form.',
    primaryLabel: 'Sign in',
    secondaryLabel: 'Cancel',
  },
  embeddedOpenForm: {
    icon: OpenInNewIcon,
    title: 'Sign in to access Asset Studio',
    body:
      'Log in to your organization account to choose an asset from your ' +
      'library and attach it to this form. You’ll be directed to a new ' +
      'page to continue.',
    primaryLabel: 'Sign in',
    secondaryLabel: 'Cancel',
  },
  noPermission: {
    icon: LockOutlinedIcon,
    title: 'Asset access is restricted',
    body:
      'Your account does not have permission to view assets for this ' +
      'organization. Contact an administrator if you need access.',
    primaryLabel: null,
    secondaryLabel: 'Close',
  },
  loadingAccount: {
    icon: null,
    title: 'Loading your account',
    body:
      'We are checking your organization and asset permissions. This should ' +
      'only take a moment.',
    primaryLabel: null,
    secondaryLabel: null,
  },
  missingOrganization: {
    icon: WarningAmberOutlinedIcon,
    title: 'Organization access required',
    body:
      'We could not find an organization for this account. Contact your ' +
      'administrator if you believe this is incorrect.',
    primaryLabel: null,
    secondaryLabel: 'Close',
  },
};

const CENTERED_CONTENT_SX = {
  minHeight: 280,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const ICON_WRAP_SX = {
  width: 48,
  height: 48,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: 'action.hover',
  color: 'text.secondary',
};

export default function AssetAccessPrompt({
  state,
  onPrimary,
  onSecondary,
  primaryDisabled = false,
  secondaryDisabled = false,
  titleId = 'asset-access-dialog-title',
  descriptionId = 'asset-access-dialog-description',
}) {
  const copy = PROMPT_COPY[state] || PROMPT_COPY.standaloneLogin;
  const Icon = copy.icon;
  const isLoading = state === 'loadingAccount';

  return (
    <>
      <DialogTitle
        id={titleId}
        sx={{
          px: 4,
          pt: 4,
          pb: 0,
          textAlign: 'center',
        }}
      >
        <Stack spacing={2} alignItems="center">
          {isLoading ? (
            <Box sx={ICON_WRAP_SX}>
              <CircularProgress size={24} />
            </Box>
          ) : Icon ? (
            <Box sx={ICON_WRAP_SX}>
              <Icon fontSize="small" />
            </Box>
          ) : null}

          <Typography
            component="span"
            variant="h6"
            sx={{
              fontWeight: 700,
              lineHeight: 1.25,
            }}
          >
            {copy.title}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: 4, py: 2 }}>
        <Box sx={CENTERED_CONTENT_SX}>
          <DialogContentText
            id={descriptionId}
            component="p"
            sx={{
              maxWidth: 480,
              mx: 'auto',
              color: 'text.secondary',
              textAlign: 'center',
              lineHeight: 1.6,
            }}
          >
            {copy.body}
          </DialogContentText>
        </Box>
      </DialogContent>

      {(copy.primaryLabel || copy.secondaryLabel) && (
        <DialogActions
          sx={{
            px: 4,
            pb: 4,
            pt: 0,
            justifyContent: 'center',
            gap: 1,
          }}
        >
          {copy.secondaryLabel && (
            <Button
              variant="text"
              color="inherit"
              onClick={onSecondary}
              disabled={secondaryDisabled}
            >
              {copy.secondaryLabel}
            </Button>
          )}

          {copy.primaryLabel && (
            <Button
              variant="contained"
              onClick={onPrimary}
              disabled={primaryDisabled}
              endIcon={
                state === 'embeddedOpenForm'
                  ? <OpenInNewIcon fontSize="small" />
                  : undefined
              }
            >
              {copy.primaryLabel}
            </Button>
          )}
        </DialogActions>
      )}
    </>
  );
}

AssetAccessPrompt.propTypes = {
  state: PropTypes.oneOf([
    'standaloneLogin',
    'embeddedOpenForm',
    'noPermission',
    'loadingAccount',
    'missingOrganization',
  ]).isRequired,
  onPrimary: PropTypes.func,
  onSecondary: PropTypes.func,
  primaryDisabled: PropTypes.bool,
  secondaryDisabled: PropTypes.bool,
  titleId: PropTypes.string,
  descriptionId: PropTypes.string,
};